import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancerOrderSummaryPage = () => {
  const [orderData, setOrderData] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'freelancer_id', direction: 'ascending' });

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const statuses = ['Assigned', 'Under Review', 'Evaluated', 'Cancelled', 'Given Up', 'Completed'];

  const statusHeadings = {
    'Assigned': 'Order IDs under Assigned',
    'Under Review': 'Order IDs Under Review',
    'Evaluated': 'Order IDs Evaluated',
    'Cancelled': 'Order IDs Cancelled',
    'Given Up': 'Order IDs Given Up',
    'Completed': 'Order IDs Completed'
  };

  useEffect(() => {
    const fetchOrderData = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(
          `${apiBaseURL}/api/freelancer-order-data`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log("📦 Raw order data received from backend:", response.data); // 🔍
        setOrderData(response.data);
      } catch (error) {
        console.error("❌ Error fetching order data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderData();
  }, [apiBaseURL]);

  useEffect(() => {
    const freelancerMap = {};

    orderData.forEach(order => {
      const { freelancer_id, freelancer_name, order_id, order_status } = order;

      if (!freelancerMap[freelancer_id]) {
        freelancerMap[freelancer_id] = {
          freelancer_id,
          freelancer_name,
          total_orders: 0,
          Assigned: [],
          'Under Review': [],
          Evaluated: [],
          Cancelled: [],
          'Given Up': [],
          Completed: []
        };
      }

      if (order_id) {
        freelancerMap[freelancer_id].total_orders += 1;
        if (statuses.includes(order_status)) {
          freelancerMap[freelancer_id][order_status].push(order_id);
        }
      }
    });

    const grouped = Object.values(freelancerMap);
    console.log("📊 Grouped Data by Freelancer:", grouped); // 🔍
    setGroupedData(grouped);
  }, [orderData]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...groupedData].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
    return 0;
  });

  console.log("📋 Sorted Data Ready for Display:", sortedData); // 🔍

  const handleDownload = () => {
    const customHeader = [
      ["OrderMinds Freelancing Solutions LLP"],
      ["Freelancer Task Summary (Grouped by Status)"],
      ["Downloaded on:", new Date().toLocaleString('en-GB')],
      []
    ];

    const columns = [
      "Freelancer ID",
      "Freelancer Name",
      "Total Orders",
      ...statuses.map(status => statusHeadings[status])
    ];

    const dataRows = sortedData.map(item => [
      item.freelancer_id,
      item.freelancer_name,
      item.total_orders,
      ...statuses.map(status => item[status].join(', ') || '-')
    ]);

    const worksheetData = [...customHeader, columns, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary");

    const timestamp = new Date().toISOString().replace(/[:T]/g, '-').split('.')[0];
    const filename = `Subscriptions_And_Payouts_${timestamp}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  if (loading) return <div>Loading order data...</div>;

  const totalFreelancers = groupedData.length;

  return (
    <div className="p-4" style={{ marginLeft: '18rem' }}>
      <h1 className="text-xl font-bold mb-1">Freelancer Task Summary (Grouped by Status)</h1>
      <p className="mb-2 text-gray-700 font-medium">Total Freelancers: {totalFreelancers}</p>

      <button
        className="mb-4 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
        onClick={handleDownload}
      >
        Download Excel
      </button>

      {sortedData.length === 0 ? (
        <p className="text-gray-500">No data found</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-auto border border-gray-400 w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-2 cursor-pointer border border-gray-400" onClick={() => handleSort('freelancer_id')}>
                  Freelancer ID {sortConfig.key === 'freelancer_id' ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽') : ''}
                </th>
                <th className="px-4 py-2 border border-gray-400">Freelancer Name</th>
                <th className="px-4 py-2 cursor-pointer border border-gray-400" onClick={() => handleSort('total_orders')}>
                  Total Orders (Count) {sortConfig.key === 'total_orders' ? (sortConfig.direction === 'ascending' ? '🔼' : '🔽') : ''}
                </th>
                {statuses.map(status => (
                  <th key={status} className="px-4 py-2 border border-gray-400">
                    {statusHeadings[status]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedData.map(item => (
                <tr key={item.freelancer_id} className="border-t border-gray-300">
                  <td className="px-4 py-2 border border-gray-400">{item.freelancer_id}</td>
                  <td className="px-4 py-2 border border-gray-400">{item.freelancer_name}</td>
                  <td className="px-4 py-2 border border-gray-400">{item.total_orders}</td>
                  {statuses.map(status => (
                    <td key={status} className="px-4 py-2 border border-gray-400">
                      {item[status].length > 0 ? item[status].join(', ') : '-'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FreelancerOrderSummaryPage;
